import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { Resource, createResourceCalendar } from '../../../store/ResourcesListStore';
import { FormatDate, formatFieldValue, formatValue } from '../../utils/common';
import { calculateSummary } from './ResourceUsageCell';
import { EntityType, mapServerEntityType } from '../../../entities/common';
import { FormatType } from '../../../entities/Metadata';
import { TenantState } from '../../../store/Tenant';
import { UserState } from '../../../store/User';
import { getDefaultCostRateTable } from '../../../store/selectors/costRateTableSelectors';
import { CalendarsState, getEntityCalendar } from '../../../store/CalendarsListStore';

type OwnProps = {
    resources: Resource[];
    startDate: Date;
    finishDate: Date;
    entityId?: string;
    entityType: EntityType;
};

type StateProps = {
    calendars: CalendarsState;
    resourcePlanningLevel: EntityType;
    tenant: TenantState;
    user: UserState;
    defaultCostRateTableId?: string;
}

type Props = OwnProps & StateProps;

const TotalTooltipContent = ({ resources, startDate, finishDate, entityId, calendars, entityType, resourcePlanningLevel, tenant, user, defaultCostRateTableId }: Props) => {
    const isSingleDay = startDate.getBeginOfDay().getTime() == finishDate.getBeginOfDay().getTime();
    const firstBaseResourceCalendar = getEntityCalendar(calendars, resources[0], resources[0].calendar.id);
    const { availability, capacityHours, committedHours, proposedHours, actualHours, committedByEntity, estimatedCharge, estimatedCost, actualCost } = resources
        .map(resource => calculateSummary(resource, startDate, finishDate, firstBaseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId))
        .reduce((cum, cur) => ({
                availability: cum.availability + cur.availability,
                capacityHours: cum.capacityHours + cur.capacityHours,
                committedHours: cum.committedHours + (entityId ? cur.committedByEntity[entityId] || 0 : cur.committedWork),
                proposedHours: cum.proposedHours + (entityId ? cur.proposedByEntity[entityId] || 0 : cur.proposedWork),
                actualHours: cum.actualHours + (entityId ? cur.actualByEntity[entityId] || 0 : cur.actualWork),
                committedByEntity: [...cum.committedByEntity, cur.committedByEntity],
                estimatedCharge: cum.estimatedCharge + (entityId ? (cur.estimatedChargeByEntity[entityId] ?? 0) : cur.estimatedCharge),
                estimatedCost: cum.estimatedCost + (entityId ? (cur.estimatedCostByEntity[entityId] ?? 0) : cur.estimatedCost),
                actualCost: cum.actualCost + (entityId ? (cur.actualCostByEntity[entityId] ?? 0) : cur.actualCost),
        }), { availability: 0, capacityHours: 0, committedHours: 0, proposedHours: 0, actualHours: 0, committedByEntity: [], estimatedCharge: 0, estimatedCost: 0, actualCost: 0 });

    const otherWork = !entityId ? undefined : committedByEntity
        .map(_ => Object.keys(_).filter(__ => __ !== entityId).reduce((cum, cur) => cum + _[cur], 0))
        .reduce((cum, cur) => cum + cur, 0);

    return <div className="timeline-tooltip total">
        <div className="content">
            {isSingleDay
                ? renderField("Date", <div className="no-overflow ellipsis">{FormatDate(startDate, { weekday: 'long' })}</div>)
                : renderField("Period", <div>{FormatDate(startDate)} - {FormatDate(finishDate)}</div>)}
            {!entityId && renderField("Availability",
                <div><span className={`progress-tip ${availability > 0 ? '' : 'gray'}`}>{formatValue(availability)}</span> hour{availability !== 1 ? 's' : ''}</div>)}
            {renderField("Capacity",
                <div>{formatValue(capacityHours)} hour{capacityHours !== 1 ? 's' : ''}</div>)}
            {renderField("Committed",
                <div><span className="progress-tip gray">
                    {formatValue(committedHours)}</span> hour{committedHours !== 1 ? 's' : ''} {entityId ? `on this ${entityType}` : ''}</div>)}
            {renderField("Proposed",
                <div><span className="progress-tip gray">
                    {formatValue(proposedHours)}</span> hour{proposedHours !== 1 ? 's' : ''} {entityId ? `on this ${entityType}` : ''}</div>)}
            {renderField("Estimated Charge", <div>{formatFieldValue(estimatedCharge, FormatType.Cost, user, entityType, tenant, "EstimatedCharge")}</div>)}
            {renderField("Estimated Cost", <div>{formatFieldValue(estimatedCost, FormatType.Cost, user, entityType, tenant, "EstimatedCost")}</div>)}
            {renderField("Actual Cost", <div>{formatFieldValue(actualCost, FormatType.Cost, user, entityType, tenant, "ActualCost")}</div>)}
            {renderField("Actual",
                <div>{formatValue(actualHours)} hour{actualHours !== 1 ? 's' : ''}</div>)}
            {!!entityId && renderField("Other Work", <div>{formatValue(otherWork)} hours</div>)}
        </div>
    </div>
}

const renderField = (name: string, valueElement: JSX.Element, className?: string): JSX.Element | null => {
    return (
        <div className={`item align-center ${className || ''}`}>
            {!!name && <div className="label">{name}</div>}
            {valueElement}
        </div>
    );
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        calendars: state.calendars,
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        tenant: state.tenant,
        user: state.user,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(TotalTooltipContent);